///
/// EarthBrowser
///
/// Created by Matt Giger on 10/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The moon: a camera-facing billboard at the moon's geocentric position
/// shaded as a sphere (moon.glsl). The disc's normals are reconstructed per
/// fragment and lit by the real sun direction, so the phase, the
/// orientation of the terminator and the libration from the camera's
/// vantage all follow from the ephemeris; the surface is the LRO color map
/// (NASA SVS CGI Moon Kit) oriented by the moon's spin axis.
///

import { cMoonRadiusKm } from "@/earthbrowser/utils/astronomy"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type matrix } from "@stovokor/math/matrix"
import { cross, dot, normal, vec2, type vec3 } from "@stovokor/math/vector"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type SkyEphemeris } from "./Sky"
import { BillboardQuad, cApparentSizeScale, cSkyOrderMoon, pixelsPerRadian, skyViewProjection } from "./SkyGeometry"

const cMoonTextureUrl = "/images/shaders/moon.jpg"
// Extra pixels around the disc for the antialiased edge
const cEdgeMarginPx = 1.5
// Light on the night side from the sunlit Earth, relative to full sunlight
// (linear radiance; the shader lights the map in linear space)
const cEarthshine = 0.008

export class Moon extends NodeBase {
	private material: MoonMaterial

	constructor(context: StovokorContext, ephemeris: SkyEphemeris) {
		super()
		this.renderOrder = cSkyOrderMoon
		this.material = new MoonMaterial(context)
		this.renderer = new MoonRenderer(context, new BillboardQuad(context), this.material, ephemeris)
	}

	public get textureLoaded(): boolean {
		return this.material.available
	}

	override destroy(): void {
		this.material.moonTexture.destroy()
		super.destroy()
	}
}

class MoonMaterial implements Material {
	public transparency: boolean = false
	public moonTexture: Texture

	constructor(context: StovokorContext) {
		this.moonTexture = new Texture(context, 1, 1,
			WebGL2RenderingContext.RGBA, WebGL2RenderingContext.RGBA, WebGL2RenderingContext.UNSIGNED_BYTE,
			WebGL2RenderingContext.LINEAR_MIPMAP_LINEAR, WebGL2RenderingContext.LINEAR,
			WebGL2RenderingContext.REPEAT, WebGL2RenderingContext.CLAMP_TO_EDGE)
		void this.moonTexture.loadUrl(cMoonTextureUrl)
	}

	public get available(): boolean {
		return this.moonTexture.available
	}
}

class MoonRenderer extends Renderer {
	private modelViewProjection = new UniformDynamic<matrix>()
	private moonPosition = new UniformDynamic<vec3>()
	private viewport = new UniformDynamic<vec2>()
	private moonRadius = new UniformDynamic<number>()
	private quadRadius = new UniformDynamic<number>()
	private cameraRight = new UniformDynamic<vec3>()
	private cameraUp = new UniformDynamic<vec3>()
	private toViewer = new UniformDynamic<vec3>()
	private moonFacing = new UniformDynamic<vec3>()
	private moonEast = new UniformDynamic<vec3>()
	private moonNorth = new UniformDynamic<vec3>()
	private sunDirection = new UniformDynamic<vec3>()
	// Bound by the Renderer base class through reflection, hence public
	public earthshine = new UniformDynamic<number>(cEarthshine)
	private ephemeris: SkyEphemeris

	constructor(context: StovokorContext, geometry: BillboardQuad, material: MoonMaterial, ephemeris: SkyEphemeris) {
		super(context, new ShaderSourceDefinition("moon"), geometry, material)
		this.ephemeris = ephemeris
		this.drawState.depthTest = false
		this.drawState.depthWrite = false
	}

	protected stateUpdate(state: RenderState): void {
		const e = this.ephemeris
		const cameraPosition = state.camera.position
		this.modelViewProjection.value = skyViewProjection(state)
		this.moonPosition.value = e.moonPosition
		this.viewport.value = new vec2(state.width, state.height)

		const toCamera = cameraPosition.subtract(e.moonPosition)
		const distanceKm = toCamera.length
		const radiusPx = Math.atan(cMoonRadiusKm / distanceKm) * cApparentSizeScale * pixelsPerRadian(state)
		this.moonRadius.value = radiusPx
		this.quadRadius.value = radiusPx + cEdgeMarginPx

		// Billboard basis for reconstructing the sphere normals
		const camera = state.camera.state
		const forward = normal(camera.forward)
		const right = normal(cross(forward, camera.up))
		this.cameraRight.value = right
		this.cameraUp.value = cross(right, forward)
		this.toViewer.value = toCamera.multiply(1 / distanceKm)

		// Selenographic frame: the near side faces the Earth's center, north
		// along the spin axis, east toward Mare Crisium (the viewer's right
		// with north up)
		const facing = normal(e.moonPosition.multiply(-1))
		const north = normal(e.moonNorth.subtract(facing.multiply(dot(e.moonNorth, facing))))
		this.moonFacing.value = facing
		this.moonNorth.value = north
		this.moonEast.value = cross(north, facing)

		// Direction from the moon to the sun (the sun is far enough that the
		// Earth-centered direction is within a hundredth of a degree)
		this.sunDirection.value = normal(e.sunPosition.subtract(e.moonPosition))
	}
}
